
import { distinct, mergeMap, filter } from 'rxjs/operators'
import { Epic } from 'interfaces/middleware'
import { combineEpics } from 'redux-observable'
import { get, post } from 'helpers/observableAjaxHelpers'
import { createTimeperiod } from 'helpers/dateTimeHelpers'
import { GET_MEMBER_NETWORTH_TREND_URL, GET_NETWORTH_SUMMARY_URL, PATCH_MEMBER_PREFERENCES_URL } from 'interfaces/ajax'
import {
  GET_MEMBER_NETWORTH_CHANGE,
  GET_NETWORTH_SUMMARY,
  getNetworthSummaryActionCreator,
  SET_NETWORTH_SUMMARY_RELATION,
  SET_NETWORTH_SUMMARY_TIMEFRAME,
  setMemberNetworthChangeActionCreator,
  setNetworthSummaryRelationActionCreator,
} from 'actions/networthActions'
import * as moment from 'moment'
import { DATE_FORMAT_ISO_DAYLESS } from 'constants/datetime'
import { NETWORTH_PAGE_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { TimeScale } from 'interfaces/ajax/base'
import { of, empty, EMPTY } from 'rxjs'
import { isRequestAction } from 'helpers/actionsHelpers'
import { isOtherPeergroupRankSelectedSelector, networthSummaryDeltaSelector, networthSummaryRankSelector } from 'selectors/networthSelectors'
import { SELECT_RELATION } from 'actions/peergroupActions'
import { byType } from 'rxOperators/byType'
import { mapSafe, mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { toDiscardCache } from 'rxOperators/toDiscardCache'
import { catchSafe } from 'rxOperators/catchSafe'
import { accountsFilterQueryParamSelector } from 'components/features/pageFilters/selectors'

const getNetworthChangeEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(GET_MEMBER_NETWORTH_CHANGE)).pipe(
      distinct(
        ({ payload }) => JSON.stringify(payload),
        action$.pipe(toDiscardCache(NETWORTH_PAGE_DISCARD_TRIGGERS)),
      ),
      mergeMap(({ payload: { timeframe }}) => {
        const { start_date, end_date } = createTimeperiod(timeframe)
        const scale: TimeScale = 'month'
        const state = store.getState()
        const requestPayload = {
          scale,
          // we need previous month data for getting change
          start_date: moment(start_date).subtract(1, 'month').format(DATE_FORMAT_ISO_DAYLESS),
          end_date,
          excluded_account_uids: accountsFilterQueryParamSelector(state),
        }

        return get(GET_MEMBER_NETWORTH_TREND_URL, requestPayload)
          .pipe(mapSafe(({ response }) =>
            setMemberNetworthChangeActionCreator(timeframe, response)
          ))
      }))

const setNetworthSummaryRelation: Epic = (action$, store) =>
  action$
    .pipe(byType(SELECT_RELATION)).pipe(
      filter(() => {
        return isOtherPeergroupRankSelectedSelector(store.getState())
      }),
      mergeMap(({ payload }) => {
        return of(setNetworthSummaryRelationActionCreator(payload))
      }))

const getNetworthSummaryEpic: Epic = (action$, store) => action$.pipe(
  byType(GET_NETWORTH_SUMMARY, SET_NETWORTH_SUMMARY_RELATION, SET_NETWORTH_SUMMARY_TIMEFRAME),
  filter((action) => {
    if (action.type === GET_NETWORTH_SUMMARY && !isRequestAction(action)) {
      return false
    }

    const state = store.getState()

    // do not request data that we already have
    if (action.type === SET_NETWORTH_SUMMARY_RELATION && networthSummaryRankSelector(state)) {
      return false
    }

    if (action.type === SET_NETWORTH_SUMMARY_TIMEFRAME && networthSummaryDeltaSelector(state)) {
      return false
    }

    return true
  }),
  mergeMap((action) => {
    const requestPayload = action.type === GET_NETWORTH_SUMMARY
      ? action.payload.params
      : action.payload

    if (!requestPayload) {
      return empty()
    }

    const state = store.getState()

    return get(GET_NETWORTH_SUMMARY_URL, {
      ...requestPayload,
      excluded_account_uids: accountsFilterQueryParamSelector(state),
    })
      .pipe(mergeMapSafe(({ response }) => {
        return of(getNetworthSummaryActionCreator.success(requestPayload, response))
      }))
  })
)

const updateNetworthSummaryPreferencesEpic: Epic = (action$) =>
  action$.pipe(
    byType(SET_NETWORTH_SUMMARY_RELATION, SET_NETWORTH_SUMMARY_TIMEFRAME),
    mergeMap((action) => {
      const filters = action.payload

      if (!filters) {
        return empty()
      }

      return post(PATCH_MEMBER_PREFERENCES_URL, {
        body: {
          networth_summary: { filters },
        },
      }).pipe(
        mergeMap(() => EMPTY),
        catchSafe(),
      )
    }),
    mergeMap(() => EMPTY),
  )

export default combineEpics(
  getNetworthChangeEpic,
  setNetworthSummaryRelation,
  getNetworthSummaryEpic,
  updateNetworthSummaryPreferencesEpic,
)
